import { useEffect, useMemo, useState } from 'react';
import { Copy, Download, Plus, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlidePanel } from '@/shared/components';
import { useSkus } from '../hooks/useWmsSkus';
import { useInventoryTotalsBySkus } from '../hooks/useWmsInventory';
import { useSkuListTable } from '../hooks/useSkuListTable';
import { SkuListView } from '../components/skus/SkuListView';
import { SkuDetailPanel } from '../components/skus/SkuDetailPanel';
import { SkuGuide } from '../components/skus/SkuGuide';
import { exportToCSV, copyToClipboard } from '../utils/skuCsvExport';
import type { Sku, SkusResponse } from '../types';

type ActiveTab = 'list' | 'archive' | 'guide';

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'archive', label: 'Archive' },
  { value: 'guide', label: 'Guide' },
];

const VALID_TABS: ActiveTab[] = ['list', 'archive', 'guide'];
const ACTIVE_TAB_KEY = 'atek_skus_active_tab';
const PANEL_STORAGE_KEY = 'atek_skus_panel_width';

export default function SkusPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'list';
  });
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create'>('view');
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  const skusQuery = useSkus({
    activated: activeTab === 'archive' ? 'false' : 'true',
  });
  const skusData = skusQuery.data as SkusResponse | undefined;
  const skus: Sku[] = skusData?.skus ?? [];

  const pageMongoIds = useMemo(
    () => skus.map((s) => s.mongoId).filter((x): x is string => Boolean(x)),
    [skus],
  );
  const wmsTotalsQuery = useInventoryTotalsBySkus(pageMongoIds);

  const table = useSkuListTable(
    skus,
    wmsTotalsQuery.data ?? {},
    wmsTotalsQuery.isLoading,
  );

  const showListToolbar = activeTab === 'list' || activeTab === 'archive';

  const openSku = (sku: Sku) => {
    setSelectedSkuId(sku.id);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const openCreate = () => {
    setSelectedSkuId(null);
    setPanelMode('create');
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) {
      setSelectedSkuId(null);
      setPanelMode('view');
    }
  };

  const handleCreated = (id: string) => {
    setSelectedSkuId(id);
    setPanelMode('view');
  };

  const handleCopy = () => {
    copyToClipboard(table.sortedSkus, table.visibleColumns, table.ctx);
    toast.success('Copied to clipboard');
  };

  const handleExportCsv = () => {
    exportToCSV(table.sortedSkus, table.visibleColumns, table.ctx, 'skus');
    toast.success(`Exported ${table.sortedSkus.length} rows`);
  };

  return (
    <div className="space-y-4">
      {/* Tab bar + toolbar */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showListToolbar && (
          <div className="flex items-center gap-2 flex-wrap pb-1.5">
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" /> Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table.columns.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={table.isVisible(col.key)}
                    onCheckedChange={() => table.toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> New SKU
            </Button>
          </div>
        )}
      </div>

      {/* Body + slide panel */}
      <div className="flex min-h-[calc(100vh-240px)]">
        <div className="flex-1 min-w-0 overflow-auto">
          {(activeTab === 'list' || activeTab === 'archive') && (
            <SkuListView
              sortedSkus={table.sortedSkus}
              visibleColumns={table.visibleColumns}
              columnFilters={table.columnFilters}
              ctx={table.ctx}
              sortKey={table.sortKey}
              sortDirection={table.sortDirection}
              isLoading={skusQuery.isLoading}
              onSort={table.handleSort}
              onSetColumnFilter={table.setColumnFilter}
              onSelectSku={openSku}
              onCreateSku={openCreate}
            />
          )}
          {activeTab === 'guide' && <SkuGuide />}
        </div>

        <SlidePanel open={panelOpen} storageKey={PANEL_STORAGE_KEY}>
          <SkuDetailPanel
            key={panelMode === 'create' ? 'create' : selectedSkuId}
            skuId={selectedSkuId}
            mode={panelMode}
            open={panelOpen}
            onOpenChange={handlePanelClose}
            onCreated={handleCreated}
          />
        </SlidePanel>
      </div>
    </div>
  );
}
